import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addDoc, collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Heading } from '../components/heading';
import { Button } from '../components/button';
import { Dialog, DialogTitle, DialogBody } from '../components/dialog';
import { Switch } from '../components/switch';
import { useUser } from '../components/UserContext';
import { useAsociados, useClients, useComisionLedger, useContracts } from '../contexts/DataContext';
import { logBitacora } from '../utils/bitacora';
import { candidatosReparto } from '../utils/reparto';
import { cancelarPropuestaReparto, enviarAprobacionReparto } from '../api/comisiones';
import { TipoAsociadoChip } from '../components/RepartoEditor';
import { estadoEnvio } from '../components/PropuestaRepartoPendiente';
import { RepartoRetroactivoDialog } from '../components/RepartoRetroactivoDialog';

const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Configuración → Asociados. Owner-only, and only when Surelia switched the
 * module on for the org (gated in Configuracion.js; rules make the directory
 * owner-write).
 *
 * The directory every reparto points at. A baja is activo=false, never a
 * delete: ledger snapshots reference the id.
 */
const ConfiguracionAsociados = () => {
  const { user, userData } = useUser();
  const { asociados, asociadosById, loading, requiereAprobacion } = useAsociados();
  const { clientsById } = useClients();
  const { contractsList } = useContracts();
  const { movements } = useComisionLedger();
  const [editando, setEditando] = useState(null); // null | {} (alta) | asociado
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [retro, setRetro] = useState(null); // { candidatos, motivo }

  // A baja (or a reactivation) changes what resolves for every cliente that
  // names this asociado. Fianzas already booked keep their cut unless the
  // owner ticks them here; whatever is left shows as "reparto anterior" on
  // each cliente's Asociados tab, so closing the dialog hides nothing.
  const ofrecerRetroactivo = (a, activo) => {
    const candidatos = candidatosReparto({
      movements,
      contratos: contractsList || [],
      clientesById: clientsById || {},
      asociadosById: { ...asociadosById, [a.id]: { ...a, activo } },
    });
    if (!candidatos.length) return;
    const motivo = `${activo ? 'Reactivación' : 'Baja'} de ${a.nombre}`;
    if (requiereAprobacion) {
      setAviso(`${candidatos.length} fianza${candidatos.length !== 1 ? 's quedan' : ' queda'} con el ` +
        `reparto anterior. Cada cliente lo muestra en su pestaña Asociados para enviarlo a aprobación.`);
      return;
    }
    setRetro({ candidatos, motivo });
  };

  const toggleActivo = async (a) => {
    setError(null);
    setAviso(null);
    const activo = a.activo === false;
    try {
      await updateDoc(doc(db, 'orgs', userData.org, 'asociados', a.id), {
        activo,
        updatedAt: serverTimestamp(),
      });
      await logBitacora({
        action: 'UPDATE',
        collection: 'asociados',
        documentId: a.id,
        changes: [{ field: 'activo', oldValue: a.activo !== false, newValue: a.activo === false }],
        metadata: {
          description: `${a.activo === false ? 'Reactivó' : 'Dio de baja'} a ${a.nombre}`,
        },
        user,
        userData,
      });
      ofrecerRetroactivo(a, activo);
    } catch (e) {
      console.error('asociado toggle failed', e);
      setError('No se pudo actualizar el asociado.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <Heading level={2} className="text-azul-surelia">Asociados</Heading>
        <Button color="surelia" onClick={() => setEditando({})}>Agregar socio o asociado</Button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
      )}
      {aviso && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">{aviso}</div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
              <th className="py-2.5 px-4">Nombre</th>
              <th className="py-2.5 px-4">Correo</th>
              <th className="py-2.5 px-4">Tipo</th>
              <th className="py-2.5 px-4">Estado</th>
              <th className="py-2.5 px-4" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="py-6 px-4 text-center text-gray-400">Cargando...</td></tr>
            )}
            {!loading && asociados.length === 0 && (
              <tr><td colSpan={5} className="py-6 px-4 text-center text-gray-400">Sin asociados</td></tr>
            )}
            {asociados.map((a) => (
              <tr key={a.id} className={`border-b border-gray-100 last:border-0 ${a.activo === false ? 'opacity-50' : ''}`}>
                <td className="py-2.5 px-4 font-medium text-gray-800">{a.nombre}</td>
                <td className="py-2.5 px-4 text-gray-600">{a.email || '—'}</td>
                <td className="py-2.5 px-4"><TipoAsociadoChip tipo={a.tipo} /></td>
                <td className="py-2.5 px-4 text-gray-600">{a.activo === false ? 'Inactivo' : 'Activo'}</td>
                <td className="py-2.5 px-4 text-right whitespace-nowrap">
                  <Button plain onClick={() => setEditando(a)}>Editar</Button>
                  <Button plain onClick={() => toggleActivo(a)}>
                    {a.activo === false ? 'Reactivar' : 'Dar de baja'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AprobacionConfig asociados={asociados} user={user} userData={userData} onEditar={setEditando} />

      <AprobacionesReparto org={userData?.org} />

      <AsociadoDialog
        asociado={editando}
        onClose={() => setEditando(null)}
        org={userData?.org}
        user={user}
        userData={userData}
      />

      <RepartoRetroactivoDialog
        isOpen={Boolean(retro)}
        onClose={() => setRetro(null)}
        candidatos={retro?.candidatos}
        motivo={retro?.motivo}
      />
    </div>
  );
};

/**
 * Aprobación por correo: when on, a reparto change is a propuesta the chosen
 * socios approve from a personal link (orgs/{org}/config/asociados).
 *
 * One switch owns the whole card. Off, nothing else shows. On, the socios of
 * the directory are the rows: tick who approves; a socio without correo
 * cannot approve and gets a shortcut to add one. The rule (one socio or all)
 * only matters with two or more aprobadores, so it only shows then.
 */
function AprobacionConfig({ asociados, user, userData, onEditar }) {
  const { aprobacion } = useAsociados();
  const [requiere, setRequiere] = useState(false);
  const [regla, setRegla] = useState('todos');
  const [aprobadores, setAprobadores] = useState([]);
  const [saving, setSaving] = useState(false);
  const [guardadoAt, setGuardadoAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setRequiere(aprobacion.requiereAprobacion === true);
    setRegla(aprobacion.regla === 'uno' ? 'uno' : 'todos');
    setAprobadores(Array.isArray(aprobacion.aprobadores) ? aprobacion.aprobadores : []);
  }, [aprobacion]);

  useEffect(() => {
    if (!guardadoAt) return undefined;
    const t = setTimeout(() => setGuardadoAt(null), 2500);
    return () => clearTimeout(t);
  }, [guardadoAt]);

  const socios = asociados.filter((a) => a.tipo === 'socio' && a.activo !== false);
  const conCorreo = socios.filter((s) => s.email);
  const elegidos = aprobadores.filter((id) => conCorreo.some((s) => s.id === id));
  const guardado = {
    requiere: aprobacion.requiereAprobacion === true,
    regla: aprobacion.regla === 'uno' ? 'uno' : 'todos',
    aprobadores: [...(aprobacion.aprobadores || [])].sort().join(','),
  };
  const cambio = requiere !== guardado.requiere
    || (requiere && (regla !== guardado.regla
      || [...aprobadores].sort().join(',') !== guardado.aprobadores));

  const toggleAprobador = (id) => {
    setAprobadores((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setError(null);
  };

  const descartar = () => {
    setRequiere(guardado.requiere);
    setRegla(guardado.regla);
    setAprobadores(Array.isArray(aprobacion.aprobadores) ? aprobacion.aprobadores : []);
    setError(null);
  };

  const guardar = async () => {
    if (requiere && elegidos.length === 0) {
      setError('Elige al menos un socio con correo.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await setDoc(doc(db, 'orgs', userData.org, 'config', 'asociados'), {
        requiereAprobacion: requiere,
        regla,
        aprobadores: elegidos,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
      }, { merge: true });
      const nombres = elegidos.map((id) => socios.find((s) => s.id === id)?.nombre).filter(Boolean).join(', ');
      await logBitacora({
        action: 'UPDATE',
        collection: 'asociados',
        documentId: 'config',
        changes: [
          { field: 'requiereAprobacion', oldValue: guardado.requiere, newValue: requiere },
          { field: 'regla', oldValue: guardado.regla, newValue: regla },
          { field: 'aprobadores', oldValue: guardado.aprobadores, newValue: elegidos.join(',') },
        ].filter((c) => c.oldValue !== c.newValue),
        metadata: {
          description: requiere
            ? `Aprobación de reparto activa (${regla === 'uno' ? 'basta un socio' : 'todos los socios'}): ${nombres}`
            : 'Desactivó la aprobación de reparto',
        },
        user,
        userData,
      });
      setGuardadoAt(Date.now());
    } catch (e) {
      console.error('aprobacion config save failed', e);
      setError('No se pudo guardar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  const estado = !guardado.requiere
    ? 'Inactiva'
    : `Activa · ${guardado.regla === 'uno' ? 'basta un socio' : 'todos los socios'}`;

  const segmento = (activo, onClick, label) => (
    <button
      type="button"
      onClick={onClick}
      disabled={saving}
      className={`px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-lg last:rounded-r-lg ${
        activo
          ? 'bg-azul-surelia text-white'
          : 'bg-white text-gray-600 hover:bg-zinc-50'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-4 px-5 py-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-azul-surelia">Aprobación por correo</h3>
          <p className="text-xs text-gray-500 mt-0.5">{estado}</p>
        </div>
        <Switch
          color="emerald"
          className="shrink-0"
          checked={requiere}
          disabled={saving}
          onChange={(v) => { setRequiere(v); setError(null); }}
        />
      </div>

      {requiere && (
        <div className="border-t border-gray-100">
          {socios.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-gray-400">
              Sin socios en el directorio.
              <button
                type="button"
                onClick={() => onEditar?.({ tipo: 'socio' })}
                className="ml-2 font-medium text-azul-surelia hover:underline"
              >
                Agregar socio
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="py-2 px-5 font-medium">Socio</th>
                  <th className="py-2 px-4 font-medium">Correo</th>
                  <th className="py-2 px-5 font-medium text-right">Aprueba</th>
                </tr>
              </thead>
              <tbody>
                {socios.map((s) => {
                  const puede = Boolean(s.email);
                  const marcado = puede && aprobadores.includes(s.id);
                  return (
                    <tr
                      key={s.id}
                      className={`border-b border-gray-100 last:border-0 ${puede ? 'cursor-pointer hover:bg-zinc-50' : ''}`}
                      onClick={() => puede && !saving && toggleAprobador(s.id)}
                    >
                      <td className="py-2.5 px-5 font-medium text-gray-800">{s.nombre}</td>
                      <td className="py-2.5 px-4 text-gray-600">
                        {puede ? (
                          s.email
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                              Sin correo
                            </span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onEditar?.(s); }}
                              className="text-xs font-medium text-azul-surelia hover:underline"
                            >
                              Agregar
                            </button>
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-5 text-right">
                        <input
                          type="checkbox"
                          disabled={!puede || saving}
                          checked={marcado}
                          onChange={() => toggleAprobador(s.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 text-azul-surelia border-gray-300 rounded disabled:opacity-40"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {elegidos.length > 1 && (
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-600">Se aplica cuando aprueba</span>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                {segmento(regla === 'uno', () => setRegla('uno'), 'Un socio')}
                {segmento(regla === 'todos', () => setRegla('todos'), 'Todos')}
              </div>
            </div>
          )}
        </div>
      )}

      {(cambio || error || guardadoAt) && (
        <div className="flex items-center justify-between gap-3 px-5 py-3 bg-zinc-50 border-t border-gray-100">
          <span className={`text-xs ${error ? 'text-red-700' : 'text-gray-500'}`}>
            {error || (guardadoAt && !cambio ? 'Guardado' : '')}
          </span>
          {cambio && (
            <div className="flex gap-2">
              <Button plain onClick={descartar} disabled={saving}>Descartar</Button>
              <Button color="surelia" onClick={guardar} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ESTADO_PROPUESTA = {
  pendiente: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-800' },
  aprobada: { label: 'Aprobada', cls: 'bg-emerald-100 text-emerald-800' },
  rechazada: { label: 'Rechazada', cls: 'bg-red-100 text-red-700' },
  cancelada: { label: 'Cancelada', cls: 'bg-zinc-100 text-zinc-600' },
};

const VOTO = {
  aprobada: { label: 'Aprobó', cls: 'text-emerald-700' },
  rechazada: { label: 'Rechazó', cls: 'text-red-600' },
  pendiente: { label: 'Falta', cls: 'text-amber-700' },
};

const fmtFechaHora = (ms) =>
  ms ? new Date(Number(ms)).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const fmtFechaCorta = (ms) =>
  ms ? new Date(Number(ms)).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) : '';

const describirPropuesta = (p) => {
  const reparto = (p.reparto || []).filter((r) => r.pct > 0);
  if (reparto.length) return reparto.map((r) => `${r.nombre || r.asociadoId} ${r.pct}%`).join(', ');
  return p.objetivo?.tipo === 'fianza' ? 'Regresar al reparto del cliente' : 'Sin reparto';
};

const MAX_PROPUESTAS = 200;

/**
 * Every propuesta de reparto of the org, newest first: when, for which
 * cliente or fianza, what was proposed, which socios had to approve and what
 * each answered. Its own listener (DataContext only carries the pending ones
 * for the banners); rules make the collection owner-read.
 */
function AprobacionesReparto({ org }) {
  const navigate = useNavigate();
  const [propuestas, setPropuestas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todas'); // todas | pendientes
  const [abierta, setAbierta] = useState(null);
  const [busy, setBusy] = useState(null); // propuestaId while re-sending or cancelling
  const [aviso, setAviso] = useState(null); // { id, texto }

  const reenviar = async (p) => {
    setBusy(p.id);
    setAviso(null);
    try {
      const r = await enviarAprobacionReparto(p.id);
      setAviso({ id: p.id, texto: `Correo enviado a ${r.sent?.length || 0} socio${r.sent?.length !== 1 ? 's' : ''}.` });
    } catch (e) {
      console.error('reparto aprobación resend failed', e);
      const motivo = e?.code === 'functions/not-found'
        ? 'la función de correo no está desplegada'
        : e?.message || e?.code || 'error desconocido';
      setAviso({ id: p.id, texto: `No se pudo enviar el correo (${motivo}).` });
    } finally {
      setBusy(null);
    }
  };

  const cancelar = async (p) => {
    setBusy(p.id);
    setAviso(null);
    try {
      await cancelarPropuestaReparto(p.id);
    } catch (e) {
      setAviso({ id: p.id, texto: e?.message || 'No se pudo cancelar la propuesta.' });
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (!org) return undefined;
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'orgs', org, 'reparto_propuestas'), orderBy('propuestoAt', 'desc'), limit(MAX_PROPUESTAS)),
      (snap) => { setPropuestas(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); },
      (e) => { console.error('propuestas listener error', e); setPropuestas([]); setLoading(false); }
    );
    return unsub;
  }, [org]);

  const visibles = useMemo(
    () => (filtro === 'pendientes' ? propuestas.filter((p) => p.estado === 'pendiente') : propuestas),
    [propuestas, filtro]
  );
  const pendientes = propuestas.filter((p) => p.estado === 'pendiente').length;

  const segmento = (activo, onClick, label) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-lg last:rounded-r-lg ${
        activo ? 'bg-azul-surelia text-white' : 'bg-white text-gray-600 hover:bg-zinc-50'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <div>
          <h3 className="text-base font-semibold text-azul-surelia">Aprobaciones</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {pendientes ? `${pendientes} pendiente${pendientes !== 1 ? 's' : ''}` : 'Nada pendiente'}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          {segmento(filtro === 'pendientes', () => setFiltro('pendientes'), 'Pendientes')}
          {segmento(filtro === 'todas', () => setFiltro('todas'), 'Todas')}
        </div>
      </div>

      <div className="border-t border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
              <th className="py-2 px-5 font-medium whitespace-nowrap">Fecha</th>
              <th className="py-2 px-4 font-medium">Cliente</th>
              <th className="py-2 px-4 font-medium">Propuesta</th>
              <th className="py-2 px-4 font-medium">Socios</th>
              <th className="py-2 px-5 font-medium text-right">Estado</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="py-6 px-5 text-center text-gray-400">Cargando...</td></tr>
            )}
            {!loading && visibles.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 px-5 text-center text-gray-400">
                  {filtro === 'pendientes' ? 'Sin propuestas pendientes' : 'Sin propuestas'}
                </td>
              </tr>
            )}
            {visibles.map((p) => {
              const estado = ESTADO_PROPUESTA[p.estado] || { label: p.estado, cls: 'bg-zinc-100 text-zinc-600' };
              const envio = estadoEnvio(p);
              const socios = Object.entries(p.aprobadores || {});
              const abiertaEsta = abierta === p.id;
              return (
                <React.Fragment key={p.id}>
                  <tr
                    className="border-b border-gray-100 last:border-0 cursor-pointer hover:bg-zinc-50 align-top"
                    onClick={() => setAbierta(abiertaEsta ? null : p.id)}
                  >
                    <td className="py-2.5 px-5 text-gray-600 whitespace-nowrap tabular-nums">{fmtFechaHora(p.propuestoAt)}</td>
                    <td className="py-2.5 px-4">
                      <div className="font-medium text-gray-800">{p.objetivo?.nombre || '—'}</div>
                      {p.objetivo?.tipo === 'fianza' && <div className="text-xs text-gray-400">Reparto de una fianza</div>}
                    </td>
                    <td className="py-2.5 px-4 text-gray-700">{describirPropuesta(p)}</td>
                    <td className="py-2.5 px-4">
                      <div className="space-y-0.5 text-xs">
                        {socios.map(([id, a]) => (
                          <div key={id} className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-gray-700">{a.nombre || id}</span>
                            <span className={`font-medium ${VOTO[a.estado]?.cls || 'text-gray-500'}`}>
                              {VOTO[a.estado]?.label || a.estado}
                            </span>
                            {a.at && <span className="text-gray-400">{fmtFechaCorta(a.at)}</span>}
                          </div>
                        ))}
                        {p.regla === 'uno' && socios.length > 1 && (
                          <div className="text-gray-400">Basta un socio</div>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-5 text-right whitespace-nowrap">
                      <span className="inline-flex flex-col items-end gap-1">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${estado.cls}`}>
                          {estado.label}
                        </span>
                        {envio && (
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${envio.cls}`}
                            title={envio.error || undefined}
                          >
                            {envio.label}
                          </span>
                        )}
                      </span>
                    </td>
                  </tr>
                  {abiertaEsta && (
                    <tr className="bg-zinc-50/60 border-b border-gray-100">
                      <td colSpan={5} className="px-5 py-3">
                        <div className="grid gap-x-8 gap-y-1 text-xs text-gray-600 sm:grid-cols-2">
                          <div>
                            <span className="text-gray-400">Antes: </span>
                            {(p.anterior || []).filter((r) => r.pct > 0).map((r) => `${r.nombre || r.asociadoId} ${r.pct}%`).join(', ') || 'Sin reparto'}
                          </div>
                          <div>
                            <span className="text-gray-400">Propuso: </span>
                            {p.propuestoPorNombre || p.propuestoPorCorreo || '—'}
                          </div>
                          <div>
                            <span className="text-gray-400">Fianzas a ajustar: </span>
                            {(p.fianzasARestatear || []).length}
                            {p.aplicada?.actualizadas != null && ` (${p.aplicada.actualizadas} ajustadas)`}
                          </div>
                          <div>
                            <span className="text-gray-400">Resuelta: </span>
                            {fmtFechaHora(p.resueltaAt)}
                          </div>
                          {p.motivo && (
                            <div className="sm:col-span-2">
                              <span className="text-gray-400">Motivo: </span>{p.motivo}
                            </div>
                          )}
                          {p.aplicacionError && (
                            <div className="sm:col-span-2 text-red-700">{p.aplicacionError}</div>
                          )}
                          {envio?.error && (
                            <div className="sm:col-span-2 text-red-700">
                              <span className="text-gray-400">Correo: </span>{envio.error}
                              {p.envio?.intentos ? ` (${p.envio.intentos} intento${p.envio.intentos !== 1 ? 's' : ''})` : ''}
                            </div>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-4 text-xs">
                          {p.estado === 'pendiente' && (
                            <>
                              <button
                                type="button"
                                disabled={busy === p.id}
                                onClick={(e) => { e.stopPropagation(); reenviar(p); }}
                                className="font-medium text-azul-surelia hover:underline disabled:opacity-50"
                              >
                                {busy === p.id ? 'Enviando…' : 'Reenviar correo'}
                              </button>
                              <button
                                type="button"
                                disabled={busy === p.id}
                                onClick={(e) => { e.stopPropagation(); cancelar(p); }}
                                className="font-medium text-gray-500 hover:underline disabled:opacity-50"
                              >
                                Cancelar propuesta
                              </button>
                            </>
                          )}
                          {p.objetivo?.clienteId && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigate(`/app/clientes/${p.objetivo.clienteId}?tab=asociados`); }}
                              className="font-medium text-azul-surelia hover:underline"
                            >
                              Ver cliente
                            </button>
                          )}
                          {aviso?.id === p.id && <span className="text-gray-600">{aviso.texto}</span>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {propuestas.length >= MAX_PROPUESTAS && (
        <div className="px-5 py-2 text-xs text-gray-400 border-t border-gray-100">
          Se muestran las últimas {MAX_PROPUESTAS}.
        </div>
      )}
    </div>
  );
}

function AsociadoDialog({ asociado, onClose, org, user, userData }) {
  const esAlta = asociado && !asociado.id;
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [tipo, setTipo] = useState('asociado');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!asociado) return;
    setNombre(asociado.nombre || '');
    setEmail(asociado.email || '');
    setTipo(asociado.tipo === 'socio' ? 'socio' : 'asociado');
    setError(null);
  }, [asociado]);

  if (!asociado) return null;

  const guardar = async () => {
    const n = nombre.trim();
    const e = email.trim().toLowerCase();
    if (!n) { setError('El nombre es obligatorio.'); return; }
    if (e && !EMAIL_RE.test(e)) { setError('El correo no es válido.'); return; }
    setSaving(true);
    setError(null);
    try {
      if (esAlta) {
        const ref = await addDoc(collection(db, 'orgs', org, 'asociados'), {
          nombre: n, email: e || null, tipo, activo: true,
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
        await logBitacora({
          action: 'CREATE', collection: 'asociados', documentId: ref.id,
          metadata: { description: `Agregó ${tipo === 'socio' ? 'al socio' : 'al asociado'} ${n}` },
          user, userData,
        });
      } else {
        const changes = [
          ['nombre', asociado.nombre, n],
          ['email', asociado.email || null, e || null],
          ['tipo', asociado.tipo, tipo],
        ].filter(([, a, b]) => a !== b).map(([field, oldValue, newValue]) => ({ field, oldValue, newValue }));
        if (changes.length) {
          await updateDoc(doc(db, 'orgs', org, 'asociados', asociado.id), {
            nombre: n, email: e || null, tipo, updatedAt: serverTimestamp(),
          });
          await logBitacora({
            action: 'UPDATE', collection: 'asociados', documentId: asociado.id, changes,
            metadata: { description: `Actualizó a ${n}` },
            user, userData,
          });
        }
      }
      onClose();
    } catch (err) {
      console.error('asociado save failed', err);
      setError('No se pudo guardar el asociado.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? () => {} : onClose} size="md">
      <DialogTitle>
        <span className="text-azul-surelia">
          {esAlta ? (tipo === 'socio' ? 'Nuevo socio' : 'Nuevo asociado') : asociado.nombre}
        </span>
      </DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nombre</label>
            <input type="text" autoFocus value={nombre} className={field}
              onChange={(ev) => { setNombre(ev.target.value); setError(null); }}
              onKeyDown={(ev) => { if (ev.key === 'Enter') guardar(); }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Correo</label>
            <input type="text" inputMode="email" value={email} className={field}
              onChange={(ev) => { setEmail(ev.target.value); setError(null); }}
              onKeyDown={(ev) => { if (ev.key === 'Enter') guardar(); }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              {[['asociado', 'Asociado'], ['socio', 'Socio']].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setTipo(v)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    tipo === v
                      ? 'border-azul-surelia bg-azul-surelia/5 text-azul-surelia'
                      : 'border-gray-300 text-gray-600 hover:border-azul-surelia/40'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button plain onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button color="surelia" onClick={guardar} disabled={saving}>
              {saving ? 'Guardando…' : esAlta ? (tipo === 'socio' ? 'Agregar socio' : 'Agregar asociado') : 'Guardar'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default ConfiguracionAsociados;
